import { Box, Card, Heading, Stack, Text } from 'braid-design-system';
import React, { type ComponentProps } from 'react';

import * as styles from './LoadingBlock.css';

export type LoadingBlockVariant = keyof typeof styles.variant;
type LoadingBlockWidth = keyof typeof styles.width;
type LoadingBlockHeight = keyof typeof styles.height;

export interface LoadingBlockProps {
  width: LoadingBlockWidth;
  variant: LoadingBlockVariant;
  height?: LoadingBlockHeight;
}

export const LoadingBlock = ({
  width,
  variant,
  height = 'standard',
}: LoadingBlockProps) => {
  const blockStyles = [
    styles.block,
    styles.width[width],
    styles.height[height],
    styles.variant[variant],
  ];

  return <Box className={blockStyles} />;
};

type HeadingLevel = ComponentProps<typeof Heading>['level'];
type TextSize = ComponentProps<typeof Text>['size'];
export type LoadingItemProps = {
  variant?: LoadingBlockVariant;
} & (
  | {
      type: 'button';
      level?: never;
      width?: never;
      size?: never;
      lines?: never;
    }
  | {
      type: 'heading';
      level: HeadingLevel;
      width?: LoadingBlockWidth;
      size?: never;
      lines?: never;
    }
  | {
      type: 'text';
      size: TextSize;
      lines?: number;
      width?: LoadingBlockWidth;
      level?: never;
    }
);

export const LoadingItem = ({
  width,
  type,
  level,
  size,
  variant = 'standard',
  lines = 1,
}: LoadingItemProps) => {
  const baseStyles = [styles.block, styles.variant[variant]];

  if (type === 'heading') {
    return (
      <Box
        component="span"
        className={[baseStyles, styles.width[width!]]}
        data={{ automation: `loading-item-${type}` }}
      >
        <Heading component="span" level={level}>
          &nbsp;
        </Heading>
      </Box>
    );
  }

  if (type === 'text') {
    return (
      <Box
        component="span"
        display="block"
        data={{ automation: `loading-item-${type}` }}
      >
        {new Array(lines).fill('').map((_, index) => (
          <Box component="span" display="block" key={`line-${index}`}>
            <Box
              component="span"
              className={[
                baseStyles,
                lines > 1 ? styles.width.full : styles.width[width!],
              ]}
            >
              <Text size={size}>&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</Text>
            </Box>
            {index < lines - 1 ? (
              <Text size={size}>
                <br />
              </Text>
            ) : null}
          </Box>
        ))}
      </Box>
    );
  }

  if (type === 'button') {
    return (
      <Box
        component="span"
        className={[baseStyles, styles.buttonWidth]}
        height="touchable"
        data={{ automation: `loading-item-${type}` }}
      >
        &nbsp;
      </Box>
    );
  }

  throw new Error('LoadingItem: No type provided');
};

export const LoadingCardItem = () => (
  <Card>
    <Stack space="medium">
      <Stack space="small">
        <LoadingItem type="heading" level="4" width="medium" />
        <LoadingItem type="text" size="standard" width="small" />
      </Stack>
      <LoadingItem type="text" size="standard" width="large" />
    </Stack>
  </Card>
);
